import React, { useEffect } from "react";
import { useApi } from "@core/api";
import { ClientOnly } from "@core/components/client-only";
import { EditorSection, useEditorFrameContext } from "@core/components/EditorFrame";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { Link, useParams } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useFormContext } from "react-hook-form";
import { ProductAttribute } from "@/apidtos";
import { Alert, AlertDescription, AlertTitle } from "@core/components/ui/alert";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@core/components/ui/table";
import { Button } from "@core/components/ui/button";

const ProductAttributeContent = () => {
  const { id: attributeId } = useParams();
  const api = useApi();

  const productAttributes = useQuery({
    queryKey: ["product-attributes", attributeId],
    queryFn: async () => {
      if (!attributeId) {
        throw new Error("Invalid attribute ID");
      }
      const attributes = await api.ListProductAttribute({
        AttributeId: Number(attributeId),
      });
      return attributes;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  if (productAttributes.isLoading || !productAttributes.data) {
    return null;
  }
  return (
    <ClientOnly>
      <InnerProductAttributeContent attributes={productAttributes.data.Results} />
    </ClientOnly>
  );
};

interface ProductAttributeContentProps {
  attributes: ProductAttribute[];
}
const InnerProductAttributeContent = ({ attributes }: ProductAttributeContentProps) => {
  const { setAccordionValue, accordionValue } = useEditorFrameContext();
  const { t } = useTranslation();

  useEffect(() => {
    if (!accordionValue.includes("Product Attributes")) {
      setAccordionValue((prev) => [...prev, "Product Attributes"]);
    }
  }, [accordionValue, setAccordionValue]);

  return (
    <EditorSection title="Product Attributes">
      {/* Insert a warning here using ShadCN (RadixUI) about how these are only for display, and to edit you must click the product link */}
      <Alert variant="warning">
        <AlertTitle>
          {t("ui.admin.productattributes.displayonly", "Display Only")}
        </AlertTitle>
        <AlertDescription>
          {t("ui.admin.productattributes.displayonlydescription", "The values on this page are informational only. To edit the values, please click the relevant product link below.")}
        </AlertDescription>
      </Alert>
      <Table className="mt-4">
        <TableHeader>
          <TableRow>
            <TableHead>{t("ui.admin.productattributes.product", "Product")}</TableHead>
            <TableHead>{t("ui.admin.productattributes.value", "Value")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {attributes.map((attribute) => (
            <TableRow key={attribute.Id}>
              <TableCell>
                <Button asChild variant="outline">
                  <Link
                    to={`/admin/inventory/products/${attribute.ProductId}`}
                    className="link"
                  >
                    {attribute.ProductId}
                  </Link>
                </Button>
              </TableCell>
              <TableCell>{attribute.Value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </EditorSection>
  );
};

registerExtension("admin.attributes.attributes_.$id.route.details", ProductAttributeContent, "ProductAttributeContent");
